import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, Shield, Clock, UserCheck, HeartHandshake, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Abortion() {
  const { t } = useLanguage();

  useEffect(() => {
    axios.post(`${API}/analytics/visit`, { page: '/abortion' }).catch(() => {});
  }, []);

  const features = [
    { icon: Shield, title: 'Health Canada Approved Pills', desc: 'We use Mifegymiso — the same medication used in hospitals — following the protocol set by the Society of Obstetricians and Gynaecologists of Canada.' },
    { icon: Clock, title: 'Usually Seen Within 3 Days', desc: 'Time matters. Once you call, we get you in for the consult quickly so the decision window is not driving the timeline.' },
    { icon: UserCheck, title: 'Doctor-Led, Start to Finish', desc: 'Dr. Ritika personally handles the initial consult, prescription, and the follow-up. No handoffs to people you have never met.' },
    { icon: HeartHandshake, title: 'Talk, Don\'t Lecture', desc: 'Whatever you decide — to go ahead, to wait, to think it over — we will listen. There is no script we are running against you.' },
  ];

  const steps = [
    { num: '01', title: 'Phone Call or Message', desc: 'A quick 5-minute call so we know what you need. No questions about why. We book the in-person visit from there.' },
    { num: '02', title: 'Consult & Ultrasound', desc: 'You come in, we date the pregnancy by ultrasound (this is required, not optional), and review your health history. Allow about an hour.' },
    { num: '03', title: 'Medication at Home', desc: 'First pill (mifepristone) is taken at the clinic. The second dose (misoprostol) is taken at home 24 to 48 hours later, when you are ready.' },
    { num: '04', title: 'Check-In After 7-14 Days', desc: 'A short follow-up — by phone or in-person, your choice — to confirm everything is complete and you are physically and emotionally okay.' },
  ];

  return (
    <div data-testid="abortion-page">
      <section className="py-24 sm:py-32 bg-secondary/50">
        <div className="container mx-auto px-6 md:px-8 text-center">
          <p className="text-sm text-muted-foreground uppercase tracking-widest mb-3">{t('abortion.title')}</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light">{t('abortion.title')}</h1>
          <p className="text-base text-muted-foreground mt-4 max-w-2xl mx-auto">{t('abortion.subtitle')}</p>
        </div>
      </section>

      <section className="py-24 sm:py-32">
        <div className="container mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((f, i) => (
              <Card key={i} className="transition-card rounded-2xl border-border text-center" data-testid={`abortion-feature-${i}`}>
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <f.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-heading text-lg font-medium">{f.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-secondary/50">
        <div className="container mx-auto px-6 md:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-normal text-center mb-16">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((s, i) => (
              <div key={i} className="relative" data-testid={`abortion-step-${i}`}>
                <span className="text-6xl font-heading font-bold text-primary/10">{s.num}</span>
                <h3 className="font-heading text-xl font-medium mt-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32">
        <div className="container mx-auto px-6 md:px-8">
          <Card className="rounded-2xl border-border overflow-hidden">
            <CardContent className="p-8 md:p-12">
              <div className="flex flex-col md:flex-row items-start gap-8">
                <div className="flex-1">
                  <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-normal">What the Day Actually Looks Like</h2>
                  <div className="mt-6 space-y-4">
                    {['You will not be asked to justify your decision to anyone here', 'Private waiting room — no shared corridors with other clinics', 'A nurse stays with you during the in-clinic dose', 'A direct phone number for after-hours questions, answered by us, not a call centre', 'Coverage by OHIP for eligible Ontario residents (we will help you check)'].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-muted-foreground">{item}</p>
                      </div>
                    ))}
                  </div>
                  <Link to="/contact">
                    <Button size="lg" className="mt-8 rounded-full px-10" data-testid="abortion-cta">{t('hero.cta')} <ArrowRight className="ml-2 w-4 h-4" /></Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
